import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../context/PermissionsContext';
import { useCustomers } from '../hooks/useCustomers';
import { subscribeToCropTypes } from '../services/cropType.service';
import { subscribeToRegions } from '../services/region.service';
import { subscribeToSeasonTimes } from '../services/seasonTime.service';
import { getAllOrgMembers } from '../services/appUser.service';
import { Navbar } from '../components/Navbar';
import { ModuleCard, PermissionGate } from '../components/core';

function getGreetingKey(): 'dashboard.greetingMorning' | 'dashboard.greetingAfternoon' | 'dashboard.greetingEvening' | 'dashboard.greetingNight' {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return 'dashboard.greetingMorning';
  if (hour >= 12 && hour < 18) return 'dashboard.greetingAfternoon';
  if (hour >= 18 && hour < 22) return 'dashboard.greetingEvening';
  return 'dashboard.greetingNight';
}

export default function DashboardPage() {
  const { user, signOut } = useAuth();
  const { org, orgId, orgSlug } = useOrg();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { customers, loading: customersLoading } = useCustomers(orgId);

  const [cropTypeCount, setCropTypeCount] = useState<number | undefined>(undefined);
  const [regionCount, setRegionCount] = useState<number | undefined>(undefined);
  const [seasonTimeCount, setSeasonTimeCount] = useState<number | undefined>(undefined);
  const [userCount, setUserCount] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (!orgId || !hasPermission('cropTypes.manage')) return;
    return subscribeToCropTypes(
      orgId,
      types => setCropTypeCount(types.length),
      () => setCropTypeCount(0),
    );
  }, [orgId, hasPermission]);

  useEffect(() => {
    if (!orgId || !hasPermission('regions.manage')) return;
    return subscribeToRegions(
      orgId,
      regions => setRegionCount(regions.length),
      () => setRegionCount(0),
    );
  }, [orgId, hasPermission]);

  useEffect(() => {
    if (!orgId || !hasPermission('seasonTimes.manage')) return;
    return subscribeToSeasonTimes(
      orgId,
      times => setSeasonTimeCount(times.length),
      () => setSeasonTimeCount(0),
    );
  }, [orgId, hasPermission]);

  useEffect(() => {
    if (!orgId || !hasPermission('users.manage')) return;
    async function load() {
      try {
        const users = await getAllOrgMembers(orgId!);
        setUserCount(users.length);
      } catch {
        setUserCount(0);
      }
    }
    load();
  }, [orgId, hasPermission]);

  return (
    <div className="min-h-screen bg-green-50">
      <Navbar
        displayName={user?.displayName ?? null}
        onSignOut={signOut}
        onLogoClick={() => navigate('/')}
        onOrgNameClick={() => navigate(`/org/${orgSlug}`)}
        onAvatarClick={() => navigate('/profile')}
        orgName={org.name}
      />

      <main className="mx-auto max-w-2xl px-4 py-6 sm:px-6">
        {/* Page heading */}
        <div className="mb-6">
          <h1 className="font-display text-xl font-semibold italic text-gray-900">
            {t('dashboard.title', {
              name: user?.displayName?.split(' ')[0] ?? user?.email ?? '',
            })}
          </h1>
          <p className="mt-1 font-body text-sm text-gray-500">
            {t(getGreetingKey())}
          </p>
        </div>

        {/* Module grid — 1 col mobile, 2 col ≥ sm */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <PermissionGate permission="customers.read">
            <ModuleCard
              icon="👥"
              iconVariant="green"
              title={t('dashboard.modules.customers.title')}
              description={t('dashboard.modules.customers.description')}
              count={customersLoading ? undefined : customers.length}
              countLabel={t('dashboard.modules.customers.countLabel')}
              onClick={() => navigate(`/org/${orgSlug}/customers`)}
            />
          </PermissionGate>
          <PermissionGate permission="cropTypes.manage">
            <ModuleCard
              icon="🌾"
              iconVariant="earth"
              title={t('dashboard.modules.cropTypes.title')}
              description={t('dashboard.modules.cropTypes.description')}
              count={cropTypeCount}
              countLabel={t('dashboard.modules.cropTypes.countLabel')}
              onClick={() => navigate(`/org/${orgSlug}/crop-types`)}
            />
          </PermissionGate>
          <PermissionGate permission="regions.manage">
            <ModuleCard
              icon="📍"
              iconVariant="blue"
              title={t('dashboard.modules.regions.title')}
              description={t('dashboard.modules.regions.description')}
              count={regionCount}
              countLabel={t('dashboard.modules.regions.countLabel')}
              onClick={() => navigate(`/org/${orgSlug}/regions`)}
            />
          </PermissionGate>
          <PermissionGate permission="seasonTimes.manage">
            <ModuleCard
              icon="📅"
              iconVariant="amber"
              title={t('dashboard.modules.seasonTimes.title')}
              description={t('dashboard.modules.seasonTimes.description')}
              count={seasonTimeCount}
              countLabel={t('dashboard.modules.seasonTimes.countLabel')}
              onClick={() => navigate(`/org/${orgSlug}/season-times`)}
            />
          </PermissionGate>
          <PermissionGate permission="cropStatus.read">
            <ModuleCard
              icon="🌿"
              iconVariant="green"
              title={t('dashboard.modules.cropStatus.title')}
              description={t('dashboard.modules.cropStatus.description')}
              countLabel={t('dashboard.modules.cropStatus.countLabel')}
              showCount={false}
              onClick={() => navigate(`/org/${orgSlug}/crop-status`)}
            />
          </PermissionGate>
          <PermissionGate permission="users.manage">
            <ModuleCard
              icon="🔑"
              iconVariant="earth"
              title={t('dashboard.modules.users.title')}
              description={t('dashboard.modules.users.description')}
              count={userCount}
              countLabel={t('dashboard.modules.users.countLabel')}
              onClick={() => navigate(`/org/${orgSlug}/users`)}
            />
          </PermissionGate>
          <PermissionGate permission="users.manage">
            <ModuleCard
              icon="⚙️"
              iconVariant="gray"
              title={t('dashboard.modules.settings.title')}
              description={t('dashboard.modules.settings.description')}
              count={org.name}
              showCount
              onClick={() => navigate(`/org/${orgSlug}/settings`)}
            />
          </PermissionGate>
        </div>
      </main>
    </div>
  );
}
